import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSubs } from "@/context/SubsContext";
import {
  toMonthly,
  toYearly,
  formatEUR,
  formatDateFR,
  daysUntil,
  aggregateByCategory,
  buildRecommendations,
  categoryLabel,
} from "@/lib/subscriptions";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  Wallet,
  Sparkles,
  Package,
  CalendarClock,
  ArrowUpRight,
  Inbox,
} from "lucide-react";

const CHART_COLORS = ["#059669", "#34d399", "#065f46", "#a7f3d0", "#10b981", "#6ee7b7", "#047857", "#d1d5db"];

function MetricCard({ icon: Icon, label, value, hint, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl bg-white p-6 border border-gray-100 shadow-soft card-hover ${accent ? "ring-1 ring-emerald-100" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${accent ? "bg-emerald-600" : "bg-emerald-50"}`}>
          <Icon size={18} strokeWidth={1.75} className={accent ? "text-white" : "text-emerald-600"} />
        </div>
      </div>
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      <div className="font-heading text-3xl font-bold text-gray-900 tracking-tight">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-2">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { subs, loading } = useSubs();

  const stats = useMemo(() => {
    const active = subs.filter((s) => s.status === "actif");
    const monthlyTotal = active.reduce((sum, s) => sum + toMonthly(s.price, s.frequency), 0);
    const yearlyTotal = active.reduce((sum, s) => sum + toYearly(s.price, s.frequency), 0);
    return { active, monthlyTotal, yearlyTotal };
  }, [subs]);

  const categoryData = useMemo(() => aggregateByCategory(subs), [subs]);

  const upcoming = useMemo(() => {
    return subs
      .filter((s) => s.status === "actif" && s.next_renewal)
      .map((s) => ({ ...s, days: daysUntil(s.next_renewal) }))
      .filter((s) => s.days !== null && s.days >= 0)
      .sort((a, b) => a.days - b.days)
      .slice(0, 5);
  }, [subs]);

  const recommendations = useMemo(() => buildRecommendations(subs).slice(0, 3), [subs]);

  const potentialSavings = useMemo(() => {
    return recommendations
      .filter((r) => Number.isFinite(r.savingsMonthly))
      .reduce((sum, r) => sum + r.savingsMonthly, 0);
  }, [recommendations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-gray-500" data-testid="dashboard-loading">
        Chargement…
      </div>
    );
  }

  if (subs.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-10 border border-gray-100 shadow-soft text-center" data-testid="dashboard-empty">
        <Inbox className="mx-auto text-emerald-500 mb-3" size={36} strokeWidth={1.5} />
        <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Aucun abonnement</h2>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          Ajoutez votre premier abonnement pour voir apparaître vos dépenses, économies potentielles et renouvellements.
        </p>
        <Link to="/abonnements">
          <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="go-add-sub">
            Ajouter un abonnement
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* Metrics */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard
          icon={Wallet}
          label="Dépenses mensuelles"
          value={formatEUR(stats.monthlyTotal)}
          hint={`${stats.active.length} abonnement${stats.active.length > 1 ? "s" : ""} actif${stats.active.length > 1 ? "s" : ""}`}
          testid="metric-monthly"
          accent
        />
        <MetricCard
          icon={TrendingUp}
          label="Coût annuel estimé"
          value={formatEUR(stats.yearlyTotal)}
          hint="Projection sur 12 mois"
          testid="metric-yearly"
        />
        <MetricCard
          icon={Sparkles}
          label="Économies identifiées"
          value={formatEUR(potentialSavings) + " /mois"}
          hint="Basé sur l'analyse de vos abonnements"
          testid="metric-savings"
        />
        <MetricCard
          icon={Package}
          label="Abonnements actifs"
          value={stats.active.length}
          hint={`${subs.length - stats.active.length} résilié${subs.length - stats.active.length > 1 ? "s" : ""}`}
          testid="metric-count"
        />
      </section>

      {/* Charts + Upcoming */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="chart-category">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-gray-900">Dépenses par catégorie</h3>
              <p className="text-sm text-gray-500">Répartition mensuelle de vos abonnements actifs</p>
            </div>
          </div>
          {categoryData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Aucune donnée</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="monthly"
                    nameKey="label"
                    animationDuration={800}
                  >
                    {categoryData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatEUR(v)}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }}
                  />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="upcoming-renewals">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-gray-900">Prochains renouvellements</h3>
              <p className="text-sm text-gray-500">Ne manquez aucune échéance</p>
            </div>
            <CalendarClock className="text-emerald-500" size={20} strokeWidth={1.5} />
          </div>
          {upcoming.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">Aucun renouvellement à venir</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {upcoming.map((s) => (
                <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{s.name}</div>
                    <div className="text-xs text-gray-500">{formatDateFR(s.next_renewal)} · {categoryLabel(s.category)}</div>
                  </div>
                  <Badge
                    variant="secondary"
                    className={`rounded-full ${
                      s.days <= 3 ? "bg-red-50 text-red-700" : s.days <= 7 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    J-{s.days}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Priority Recommendations */}
      <section className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="priority-recs">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-heading text-lg font-bold text-gray-900">Recommandations prioritaires</h3>
            <p className="text-sm text-gray-500">Analyse basée sur vos données réelles</p>
          </div>
          <Link to="/economies" className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid="see-all-recs">
            Tout voir <ArrowUpRight size={16} />
          </Link>
        </div>
        {recommendations.length === 0 ? (
          <div className="text-sm text-gray-400 py-6 text-center">Aucune recommandation pour le moment.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendations.map((r) => (
              <div key={r.id} className="rounded-xl p-5 bg-emerald-50/50 border border-emerald-100">
                <Badge className="rounded-full bg-emerald-600 text-white mb-3 capitalize">{r.type}</Badge>
                <div className="font-heading font-bold text-gray-900 mb-1">{r.title}</div>
                <p className="text-xs text-gray-600 leading-relaxed">{r.description}</p>
                {Number.isFinite(r.savingsMonthly) && r.savingsMonthly > 0 && (
                  <div className="mt-3 text-xs text-emerald-700 font-semibold">
                    Économie estimée : {formatEUR(r.savingsMonthly)} / mois
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
